import { fromJS, List, Record } from 'immutable';
import { startsWith } from 'lodash';
import { v4 } from 'uuid';
import {
  generateKeys,
  generateSchema,
  setInModel,
} from '../../../utils/helpers/model.helper';
import { ItemUnitTypesEnum } from '../../item-data/item-data.data';
import { DocumentReferenceTypesEnum } from '../document-data.data';
import { AppliedTax } from './applied-tax.model';
import {
  DEFAULT_DISCOUNT_TYPE,
  Discount,
  DiscountTypesEnum,
} from './discount.model';
import { Reference } from './reference.model';

export enum MarkupTypesEnum {
  amount = 'amount',
  percentage = 'percentage',
}

export const DEFAULT_UNIT_TYPE: string = ItemUnitTypesEnum.parts;

export const DOCUMENT_ITEM_SCHEMA = Object.freeze({
  ID: '',
  LOCAL_ID: '', // non-existing from server response
  DESCRIPTION: '',
  NAME: '',
  QTY: 1,
  UNIT_PRICE: 0,
  CODE: null, // ''
  UNIT_TYPE: DEFAULT_UNIT_TYPE,
  APPLIED_TAXES: List<AppliedTax>([]),
  WITHHOLDING_TAX_APPLIES: false,
  DISCOUNT: Discount,
  REFERENCE: null, // Reference
});
const RECORD = generateSchema(DOCUMENT_ITEM_SCHEMA);
const KEYS: any = generateKeys(DOCUMENT_ITEM_SCHEMA);

export class DocumentItem extends Record(RECORD) {
  static KEYS: any = KEYS;

  constructor(props?: any) {
    props = props && props.toMap ? props.toMap() : fromJS(props || {});

    if (!props.get(KEYS.ID)) {
      props = props.set(KEYS.ID, v4());
    }

    if (!props.get(KEYS.LOCAL_ID)) {
      props = props.set(KEYS.LOCAL_ID, v4());
    }

    super(props);

    const selfModel = setInModel(this, [
      {
        key: KEYS.APPLIED_TAXES,
        value: props.get(KEYS.APPLIED_TAXES)
          ? this.appliedTaxes.map((params) => new AppliedTax(params))
          : List<AppliedTax>([]),
      },
      {
        key: KEYS.DISCOUNT,
        value: new Discount(this.discount),
      },
      {
        key: KEYS.REFERENCE,
        value: props.get(KEYS.REFERENCE) ? new Reference(this.reference) : null,
      },
      {
        key: KEYS.WITHHOLDING_TAX_APPLIES,
        value: props.get(KEYS.WITHHOLDING_TAX_APPLIES)
          ? this.withholdingTaxApplies
          : false,
      },
    ]);

    return selfModel as DocumentItem;
  }

  get id(): string {
    return this.get(KEYS.ID);
  }

  get localId(): string {
    return this.get(KEYS.LOCAL_ID);
  }

  get description(): string {
    return this.get(KEYS.DESCRIPTION);
  }

  get name(): string {
    return this.get(KEYS.NAME);
  }

  get quantity(): number {
    return this.get(KEYS.QTY);
  }

  get unitPrice(): number {
    return this.get(KEYS.UNIT_PRICE);
  }

  get code(): string {
    return this.get(KEYS.CODE);
  }

  get withholdingTaxApplies(): boolean {
    return this.get(KEYS.WITHHOLDING_TAX_APPLIES);
  }

  get discountType(): string {
    let discount: Discount = this.discount;

    return discount ? discount.type : DEFAULT_DISCOUNT_TYPE;
  }

  get discountPercentage(): number {
    return this.discount ? this.discount.percentage : 0;
  }

  get discountAmount(): number {
    return this.discount ? this.discount.amount : 0;
  }

  get unitType(): ItemUnitTypesEnum {
    return this.get(KEYS.UNIT_TYPE);
  }

  get appliedTaxes(): List<AppliedTax> {
    return this.get(KEYS.APPLIED_TAXES);
  }

  get discount(): Discount {
    return this.get(KEYS.DISCOUNT);
  }

  get reference(): Reference {
    return this.get(KEYS.REFERENCE);
  }

  get referenceId(): string {
    return this.reference ? this.reference.id : '';
  }

  get referenceLocalId(): string {
    return this.reference ? this.reference.referenceLocalId : '';
  }

  hasDiscount(): boolean {
    return this.discount.type !== DiscountTypesEnum.no_discount;
  }

  isItemReference(): boolean {
    return this.reference
      ? this.reference.type === DocumentReferenceTypesEnum.product
      : null;
  }

  isExpenseReference(): boolean {
    return this.reference
      ? this.reference.type === DocumentReferenceTypesEnum.expense
      : null;
  }

  isTrackedTimeReference(): boolean {
    return this.reference
      ? this.reference.type === DocumentReferenceTypesEnum.tracked_time
      : null;
  }

  isAppointmentReference(): boolean {
    return this.reference
      ? this.reference.type === DocumentReferenceTypesEnum.appointment
      : null;
  }

  isPart(): boolean {
    return startsWith(this.unitType, 'parts');
  }

  isLabor(): boolean {
    return startsWith(this.unitType, 'labor');
  }

  serialize(): DocumentItem {
    return this.toMap().remove(KEYS.LOCAL_ID) as DocumentItem;
  }
}
